/**
 * Topics: subject matter that surfaces whoever sent it
 * (docs/2026-09-23-topic-priorities.md). The editor for one scope. A topic reads
 * messages that would otherwise be dropped, so the copy says few and specific
 * beats many and broad.
 */
import { useEffect, useState } from "preact/hooks";

export interface TopicLine { id?: string; name: string; match: string[]; note?: string }

export interface TopicEditorProps {
  entries: TopicLine[];
  onSave: (entries: TopicLine[]) => Promise<void>;
  /** Only corporate and personal topics reach a bulk sender; the rest is said in copy. */
  scope: "corporate" | "program" | "unit" | "person";
}

const REACH: Record<TopicEditorProps["scope"], string> = {
  corporate: "These reach furthest: they can keep a message from a no-reply address, which a program or unit topic cannot.",
  person: "Yours alone, and they reach as far as a corporate topic does: they can keep a message from a no-reply address.",
  program: "These raise a message that was kept. To keep one from a no-reply address, the phrase has to be in a corporate topic.",
  unit: "These raise a message that was kept. To keep one from a no-reply address, the phrase has to be in a corporate topic.",
};

export function TopicEditor({ entries, onSave, scope }: TopicEditorProps) {
  const [rows, setRows] = useState<{ name: string; phrases: string; note: string }[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRows(entries.length
      ? entries.map((e) => ({ name: e.name, phrases: e.match.join("\n"), note: e.note ?? "" }))
      : [{ name: "", phrases: "", note: "" }]);
  }, [entries]);
  const set = (i: number, k: "name" | "phrases" | "note", v: string) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const kept = rows
    .map((r) => ({ name: r.name.trim(), match: r.phrases.split("\n").map((p) => p.trim()).filter(Boolean), note: r.note.trim() }))
    .filter((r) => r.name && r.match.length);
  const started = rows.filter((r) => r.name.trim() || r.phrases.trim());
  const ok = kept.length === started.length;
  const save = async () => {
    setBusy(true);
    try { await onSave(kept.map((r) => ({ name: r.name, match: r.match, ...(r.note ? { note: r.note } : {}) }))); }
    finally { setBusy(false); }
  };
  return (
    <div class="inline-form topic-edit">
      <div class="s">{REACH[scope]} A topic reads messages that would otherwise be dropped, so few and specific beats many and broad.</div>
      {rows.map((r, i) => (
        <div class="topic-row" key={i}>
          <label class="sr-only" for={`tn-${i}`}>Topic name</label>
          <input id={`tn-${i}`} type="text" value={r.name} placeholder="What you call it, e.g. CMS mandated training"
            onInput={(e) => set(i, "name", (e.target as HTMLInputElement).value)} />
          <label class="sr-only" for={`tp-${i}`}>Phrases to look for, one per line</label>
          <textarea id={`tp-${i}`} rows={3} value={r.phrases} placeholder={"Phrases to look for, one per line\nsecurity awareness training\nmandated training"}
            onInput={(e) => set(i, "phrases", (e.target as HTMLTextAreaElement).value)} />
          <label class="sr-only" for={`tnote-${i}`}>Why it matters</label>
          <input id={`tnote-${i}`} type="text" value={r.note} placeholder="Why it matters (optional)"
            onInput={(e) => set(i, "note", (e.target as HTMLInputElement).value)} />
          <button class="link" aria-label={`Remove ${r.name.trim() || `topic ${i + 1}`}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <div class="buttons">
        <button class="link" onClick={() => setRows([...rows, { name: "", phrases: "", note: "" }])}>Add a topic</button>
        <button class="btn" disabled={busy || !ok} onClick={() => void save()}>Save</button>
        {!ok && <span class="muted">Every topic needs a name and at least one phrase.</span>}
      </div>
    </div>
  );
}

export interface TopicsScreenProps {
  entries: TopicLine[];
  onSave: (entries: TopicLine[]) => Promise<void>;
  notice?: { text: string; error?: boolean } | undefined;
}

export function TopicsScreen({ entries, onSave, notice }: TopicsScreenProps) {
  return (
    <main class="main settings">
      <div class="page-head">
        <span class="label">Corporate · everyone in Trellis</span>
        <h1 class="h-lg">What must never be missed</h1>
        <p class="lede">A VIP says whose mail is always read. A topic says what is always read, whoever sent it: a training deadline from a no-reply address is still a deadline. Program and unit topics are edited on Structure; your own are on your record.</p>
        {notice && <div class={`notice${notice.error ? " error" : ""}`}>{notice.text}</div>}
      </div>
      <section class="panel">
        <TopicEditor entries={entries} onSave={onSave} scope="corporate" />
      </section>
    </main>
  );
}
